'use client';

import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreExtendedUserAccessLevel,
} from '@ps/models/lib/creator-subscription';
import currency from 'currency.js';

import { useBioUserAccess } from '../../../bio/contexts/user-access/useBioUserAccess';
import { checkoutUrlBuilder } from '../../../checkout/builders';
import { ExpressApplePayGate } from '../../../checkout/components/express-apple-pay/ExpressApplePayGate';
import type { CheckoutIntent } from '../../../checkout/types/CheckoutIntent';
import { useCoursePurchased } from '../../../series/shared/contexts/course-purchase/context';
import { isClipLockedForViewer } from '../../../series/shared/functions/seriesClipAccess';
import { courseUnlockConfig } from '../../../series/visitor/functions/courseUnlockConfig';
import type { CourseDetailViewModel } from '../../../series/visitor/types/SeriesDetailViewModels';

interface CourseUnlockBannerProps {
  course: CourseDetailViewModel;
  /**
   * Bio facts — the creator identifier (`urlCode` / `creatorId`) for the purchase
   * + subscribe checkout URLs, the course return URL, and owner-bypass.
   */
  bioContext: BioContext;
  /** The course's own slug (for the return URL). */
  courseSlug: string;
  /**
   * The server-resolved course checkout intent for the express "Buy with Apple Pay"
   * button — non-null only when the course is purchasable. Rendered beside the
   * one-shot Purchase CTA.
   */
  courseIntent: CheckoutIntent | null;
}

/**
 * Visitor "Unlock this course" banner — a client island under the course hero. A
 * course unlocks two independent ways (both editor-configured, see
 * {@link courseUnlockConfig}); this shows a CTA for each the viewer lacks:
 *
 *  - **Purchase** (one-shot, the derived listing) → soft-navigates STRAIGHT to the
 *    checkout modal, returning here on success (`redirect_to`, which also drives
 *    the full-page/non-modal continuation).
 *  - **Subscribe** → soft-navigates to the shareable `/<url_code>/subscribe` picker
 *    floored at the course's tier, returning here after checkout.
 *
 * Access = purchased OR subscription-permitted. Owner + a subscription-permitted
 * viewer see no banner (they can watch). It re-resolves the viewer's standing when
 * this creator's subscription checkout completes (via {@link useOnCheckoutSuccess})
 * so the banner hides on unlock without a reload.
 *
 * NOTE: purchase-ownership isn't yet queryable client-side (the user-orders search
 * API isn't in the generated client), so after a one-shot purchase the CTA is
 * dropped only for the session (`purchased`); a fresh reload re-shows it until a
 * persistent ownership check + purchase-aware clip gate land.
 */
export function CourseUnlockBanner({
  course,
  bioContext,
  courseSlug,
  courseIntent,
}: CourseUnlockBannerProps) {
  const creatorId = bioContext.creatorId;
  const router = useRouter();
  const {
    currentUserId,
    extendedSubscriptionLevel,
    loading: accessLoading,
  } = useBioUserAccess();

  const config = courseUnlockConfig(course);
  const courseUrl = bioUrl(bioContext).course(courseSlug);

  // Purchase ownership, keyed by the course's derived `listingId` and shared
  // from the bio-wide `CoursePurchaseProvider` (one deduped request; survives
  // reload). The provider also re-resolves on a `course` checkout success, so a
  // just-completed purchase flips this to owned in the same session — the banner
  // hides with no reload (RN `coursePurchased$` parity).
  const ownership = useCoursePurchased(course.listingId);
  const purchased = ownership.purchased;

  const isOwner = currentUserId != null && currentUserId === creatorId;
  const subscriptionPermitted =
    config.subscriptionGated &&
    !isClipLockedForViewer(
      config.requiredAccessLevel,
      extendedSubscriptionLevel,
    );

  // Both unlock paths render whenever the course offers them — each button
  // reflects its OWN status (Purchase → "Purchased" when bought; Subscribe →
  // "Subscribed" when the tier is met), matching RN's `UnlockCourseButtons`.
  // The banner is NOT hidden on purchase/subscribe: a subscriber can still buy
  // standalone, and a purchaser still sees their standing — RN keeps both
  // buttons visible with disabled/status labels.
  const showPurchase = config.purchasable;
  const showSubscribe = config.subscriptionGated;

  // Subscribe-button state, 1:1 with RN's `UnlockCourseButtons` three-state
  // label. RN keys "upgrade" off the viewer holding a PAID subscription that is
  // BELOW the TOP tier (VIP) — NOT below the course's own tier — and checks it
  // BEFORE "Subscribed" (`isUpgradeRequired ? 'Upgrade Subscription' :
  // isAccessible ? 'Subscribed' : 'Subscribe'`, with `desiredAccessLevel = VIP`
  // hardcoded). So a viewer on a lower paid tier (e.g. Subscriber viewing a
  // Subscriber-gated course) still reads "Upgrade Subscription" — they can move
  // up to VIP — rather than "Subscribed". Only a VIP subscriber sees
  // "Subscribed". The free FOLLOWER tier is excluded (reads "Subscribe").
  const isUpgradeRequired =
    extendedSubscriptionLevel != null &&
    extendedSubscriptionLevel !== PopStoreExtendedSubscriptionLevel.FOLLOWER &&
    isClipLockedForViewer(
      PopStoreExtendedUserAccessLevel.VIP,
      extendedSubscriptionLevel,
    );
  const subscribeLabel = isUpgradeRequired
    ? 'Upgrade Subscription'
    : subscriptionPermitted
      ? 'Subscribed'
      : 'Subscribe';

  // Hold until the viewer's standing (subscription + purchase ownership) has
  // resolved, so the button labels don't flash the wrong state before it lands.
  if (accessLoading || !ownership.resolved) {
    return null;
  }

  // Hide the banner when nothing actionable remains:
  //  - the owner (owns everything);
  //  - no unlock path at all;
  //  - a subscription-only course (no standalone purchase) the viewer already
  //    has the tier for — RN's block is price-gated, so a permitted subscriber
  //    of a non-purchasable course sees no banner (no lone "Subscribed" chip).
  // A PURCHASABLE course is kept even when subscribed, so a subscriber can still
  // buy it standalone (RN screenshot: "Purchase for $X" + "Subscribed").
  if (
    isOwner ||
    (!showPurchase && !showSubscribe) ||
    (subscriptionPermitted && !showPurchase)
  ) {
    return null;
  }

  const onPurchase = () => {
    const url = checkoutUrlBuilder()
      .course(intent => intent.urlCode(bioContext.urlCode).slug(courseSlug))
      .onSuccess(hooks => hooks.redirectTo(courseUrl))
      .url();
    router.push(url);
  };

  const onSubscribe = () => {
    router.push(
      bioUrl(bioContext).subscribe({
        // A gated course's `requiredAccessLevel` is a subscription tier string
        // (`subscriber`/`fan`/`vip`) — a valid extended level. Floor the picker
        // at it ONLY when the viewer isn't already permitted; a viewer whose tier
        // already unlocks the course is upgrading, so leave the floor off and let
        // them pick any higher plan (RN keeps the Subscribe button live for this).
        minLevel: subscriptionPermitted
          ? undefined
          : (config.requiredAccessLevel as
              | PopStoreExtendedSubscriptionLevel
              | undefined),
        redirectTo: courseUrl,
      }),
    );
  };

  const buttonClass =
    'border-brand text-brand-text hover:bg-brand/10 flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-4 py-3 text-cta transition-colors';
  // A settled status button (Purchased) is disabled + dimmed and drops the
  // hover affordance, matching RN's `disabled` touchable. Only the Purchase
  // button ever reaches this state — the Subscribe button stays live so a
  // lower-tier subscriber can always upgrade (RN parity).
  const disabledClass = 'cursor-default opacity-60 hover:bg-transparent';

  return (
    <section className="mx-auto w-full max-w-[936px] px-4 pb-2 pt-5">
      <h2 className="text-content-1 text-sub mb-2.5">Unlock this course:</h2>
      <div className="flex flex-col gap-3 sm:flex-row">
        {/* Purchase — always shown for a standalone-sale course. Once bought it
            becomes a disabled "Purchased" status button (RN parity), NOT hidden:
            a subscriber can still see they've also purchased. */}
        {showPurchase && (
          <button
            type="button"
            onClick={onPurchase}
            disabled={purchased}
            aria-label={
              purchased
                ? 'Course already purchased'
                : `Purchase course for ${currency(config.effectivePrice ?? 0).format()}`
            }
            className={`${buttonClass} ${purchased ? disabledClass : ''}`}
          >
            {purchased ? (
              <span>Purchased</span>
            ) : (
              <>
                <span>Purchase for</span>
                <span className="font-bold">
                  {currency(config.effectivePrice ?? 0).format()}
                </span>
                {config.originalPrice != null && (
                  <span className="text-raise/40 line-through">
                    {currency(config.originalPrice).format()}
                  </span>
                )}
              </>
            )}
          </button>
        )}
        {/* Subscribe — always shown for a gated course; the label reflects the
            viewer's standing ("Subscribed" / "Upgrade Subscription" /
            "Subscribe"). NEVER disabled (RN parity: `ButtonOutlined` takes no
            `disabled`): a viewer on a lower paid tier — or even one already
            permitted — can always open the picker to move to a higher plan. */}
        {showSubscribe && (
          <button
            type="button"
            onClick={onSubscribe}
            aria-label={subscribeLabel}
            className={buttonClass}
          >
            {subscribeLabel}
          </button>
        )}
      </div>
      {/* Express one-shot purchase via Apple Pay (device-gated; renders nothing
          when unavailable). Only for an UNPURCHASED purchasable course — once
          bought there's nothing left to buy. */}
      {showPurchase && !purchased && courseIntent && (
        <div className="mt-3">
          <ExpressApplePayGate intent={courseIntent} borderRadius={8} />
        </div>
      )}
    </section>
  );
}
